import { projectCategories } from './projectCategories.js';

/**
 * Simple Categorization Helper - API Only
 * Only uses categories provided by the API, no complex computation
 */

/**
 * Check if an application has a valid existing category
 * @param {Object} app - Application object
 * @returns {boolean} - True if app has a valid category
 */
export function hasValidCategory(app) {
  if (!app.category) {
    return false;
  }
  
  // Check if the category exists in our project categories
  const cleanCategory = app.category.replace('__low', '').trim();
  const lowerCleanCategory = cleanCategory.toLowerCase();
  
  // Check alias mapping first
  if (CATEGORY_ALIASES[lowerCleanCategory]) {
    return true;
  }
  
  const foundById = projectCategories.find(cat => cat.id === cleanCategory);
  const foundByLabel = projectCategories.find(cat => cat.label.toLowerCase() === lowerCleanCategory);
  
  return !!(foundById || foundByLabel);
}

// Add alias mapping for alternate/legacy category names
const CATEGORY_ALIASES = {
  "renewable energy, ev & heat pumps": "renewables_energy",
  "short term lets": "short_term_lets",
  "short-term lets": "short_term_lets",
  // Add more aliases as needed
};

/**
 * Normalize a category to our internal ID system
 * @param {string} category - Category to normalize
 * @returns {string} - Normalized category ID
 */
export function normalizeCategory(category) {
  if (!category) return 'uncategorized';
  
  const cleanCategory = category.replace('__low', '').trim();
  const lowerCleanCategory = cleanCategory.toLowerCase();

  // Alias mapping
  if (CATEGORY_ALIASES[lowerCleanCategory]) {
    return CATEGORY_ALIASES[lowerCleanCategory];
  }

  // Try to find by ID first (case-sensitive for IDs)
  const foundById = projectCategories.find(cat => cat.id === cleanCategory);
  if (foundById) {
    return foundById.id;
  }
  
  // Try to find by label (case-insensitive for labels)
  const foundByLabel = projectCategories.find(cat => cat.label.toLowerCase() === lowerCleanCategory);
  if (foundByLabel) {
    return foundByLabel.id;
  }
  
  // Return uncategorized if not found
  return 'uncategorized';
}

/**
 * Simple API-only categorization function
 * @param {Object} app - Application object
 * @returns {Object} - Object with category information
 */
export function apiOnlyCategorizeApplication(app) {
  // Only use API category, no computation
  if (app.category && hasValidCategory(app)) {
    const normalizedCategory = normalizeCategory(app.category);
    return {
      primaryCategory: normalizedCategory,
      secondaryCategories: [],
      lowConfidence: false,
      source: 'api',
      originalApiCategory: app.category
    };
  }
  
  // Default to uncategorized if no valid API category
  return {
    primaryCategory: 'uncategorized',
    secondaryCategories: [],
    lowConfidence: false,
    source: 'uncategorized',
    originalApiCategory: app.category || null
  };
}

/**
 * Separate applications into categorized and uncategorized streams (API only)
 * @param {Array} applications - Array of applications
 * @returns {Object} - Object with categorized and uncategorized arrays
 */
export function separateApplicationStreams(applications) {
  const categorized = [];
  const uncategorized = [];
  
  applications.forEach(app => {
    if (app.category && hasValidCategory(app)) {
      // Has valid API category - use it directly
      const normalizedApp = {
        ...app,
        category: normalizeCategory(app.category),
        originalCategory: app.category,
        categorySource: 'api'
      };
      categorized.push(normalizedApp);
    } else {
      // No valid API category - mark as uncategorized
      uncategorized.push({
        ...app,
        category: 'uncategorized',
        categorySource: 'uncategorized'
      });
    }
  });
  
  return { categorized, uncategorized };
}

/**
 * Apply API-only categorization to applications
 * @param {Array} applications - Array of applications
 * @returns {Array} - Array of applications with API categories applied
 */
export function applyApiOnlyCategorization(applications) {
  return applications.map(app => {
    const { primaryCategory, source } = apiOnlyCategorizeApplication(app);
    return {
      ...app,
      category: primaryCategory, // Ensure category is always set
      categorySource: source,
      primaryCategory: primaryCategory // For backward compatibility
    };
  });
}

/**
 * Get categorization statistics (simplified)
 * @param {Array} applications - Applications to analyze
 * @returns {Object} - Statistics object
 */
export function getCategorizationStats(applications) {
  const total = applications.length;
  let categorized = 0;
  let uncategorized = 0;
  
  const categoryCount = {};
  
  applications.forEach(app => {
    if (app.category && app.category !== 'uncategorized') {
      categorized++;
      const category = app.category.replace('__low', '');
      categoryCount[category] = (categoryCount[category] || 0) + 1;
    } else {
      uncategorized++;
    }
  });
  
  return {
    total,
    categorized,
    uncategorized,
    categoryCount,
    categorizationRate: total > 0 ? (categorized / total) * 100 : 0
  };
}

// Legacy function names for backward compatibility - but simplified
export const enhancedCategorizeApplication = apiOnlyCategorizeApplication;
export const enhancedSeparateApplicationStreams = separateApplicationStreams;
export const applyLightweightCategorization = applyApiOnlyCategorization;

// Stub functions that no longer do complex processing
export function applyFullCategorization(app) {
  return Promise.resolve(apiOnlyCategorizeApplication(app));
}

export function batchProcessCategorization(applications, onProgress = () => {}, onComplete = () => {}) {
  // No longer do background processing - just apply API categories immediately
  const results = applyApiOnlyCategorization(applications);
  onProgress(applications.length, applications.length);
  onComplete(results);
  return Promise.resolve(results);
}

export function mergeApplicationStreams(categorizedApps, processedApps) {
  // Simple merge without deduplication since we're not doing complex processing
  return [...categorizedApps, ...processedApps];
} 
